import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, Routes, Route, Link, Navigate } from "react-router-dom";

// ────────────────────────────────────────────────────────────────────
// Legal compliance pages — required by PIPEDA / CASL / provincial
// consumer-protection rules. Operator disclosure lives in
// `src/legal/legal.config.js`; fill in the [BRACKETS] before public
// launch per /app/LEGAL_SETUP.md.
// ────────────────────────────────────────────────────────────────────
import PrivacyPolicy       from "./legal/PrivacyPolicy";
import TermsOfService      from "./legal/TermsOfService";
import CookiePolicy        from "./legal/CookiePolicy";
import CommunityGuidelines from "./legal/CommunityGuidelines";
import ContactInfo         from "./legal/ContactInfo";
import SubscriptionTerms   from "./legal/SubscriptionTerms";
import WithdrawalPolicy    from "./legal/WithdrawalPolicy";
import CookieConsent       from "./legal/CookieConsent";
import LegalFooter         from "./legal/LegalFooter";
import AiConsentModal      from "./components/AiConsentModal";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { FeatureFlagsProvider } from "./context/FeatureFlagsContext";
import { fetchOnboardingState } from "./lib/api";
import Navbar from "./components/Navbar";
import Hero from "./components/Hero";
import AuthModal from "./components/AuthModal";
import ProfilePanel from "./components/ProfilePanel";
import LeagueDetailModal from "./components/LeagueDetailModal";
import WhatsNewModal from "./components/WhatsNewModal";
import OnboardingWelcomeModal from "./components/OnboardingWelcomeModal";
import MatchmakingPage from "./components/MatchmakingPage";
import LeaguesPage from "./components/LeaguesPage";
import CategoryPage from "./components/CategoryPage";
import ContactPage from "./components/ContactPage";
import DeveloperPortalPage from "./components/DeveloperPortalPage";
import DeveloperSettingsPage from "./components/DeveloperSettingsPage";
import PlayerDashboard from "./components/PlayerDashboard";
import InvitePage from "./components/InvitePage";
import TeamInvitePage from "./components/TeamInvitePage";
import LeagueBySlugPage from "./components/LeagueBySlugPage";
import RefLandingPage from "./components/RefLandingPage";
import GrantRedeemPage from "./components/GrantRedeemPage";
import HqDiscoveryPage from "./components/HqDiscoveryPage";
import LeagueLadderPage from "./components/LeagueLadderPage";
import TeamPage from "./components/TeamPage";
import PlayerPublicPage from "./components/PlayerPublicPage";
import PublicOperatorPage from "./components/PublicOperatorPage";
import AdminPanel from "./components/AdminPanel";
import SeasonPassPage from "./components/SeasonPassPage";
import WalletPage from "./components/WalletPage";
import { CommandPalette } from "./components/CommandPalette";
import AdBanner from "./components/AdBanner";
import MobileBottomNav from "./components/MobileBottomNav";
import PWAInstallPrompt from "./components/PWAInstallPrompt";
import { Toast } from "./components/Toast";
import { ensureServiceWorker, pushSupported } from "./lib/push";
import { isProfileComplete } from "./lib/profile";

function AuthCallback({ onDone }) {
  const { processEmergentSession } = useAuth();
  const navigate = useNavigate();
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    const hash = window.location.hash;
    const m = hash.match(/session_id=([^&]+)/);
    if (!m) {
      navigate("/", { replace: true });
      return;
    }
    const session_id = m[1];
    (async () => {
      try {
        await processEmergentSession(session_id);
        // Clean fragment & redirect to home
        window.history.replaceState(null, "", window.location.pathname);
        onDone && onDone("Welcome to the arena.");
        navigate("/", { replace: true });
      } catch {
        navigate("/?auth_error=session_exchange_failed", { replace: true });
      }
    })();
  }, [navigate, processEmergentSession, onDone]);

  return (
    <div className="min-h-screen flex items-center justify-center page-z">
      <div className="text-center">
        <div className="loader-ring mx-auto" />
        <p className="mt-6 text-[#14b8a6] tracking-widest font-display">SYNCING OPERATOR</p>
      </div>
    </div>
  );
}

function Shell({ children, isHome }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [authOpen, setAuthOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [profileInitialTab, setProfileInitialTab] = useState(null);
  const [onboardingMode, setOnboardingMode] = useState(false);
  const [toast, setToast] = useState("");
  const [createLeagueTrigger, setCreateLeagueTrigger] = useState(0);
  const [openLeagueId, setOpenLeagueId] = useState(null);
  const [whatsNewOpen, setWhatsNewOpen] = useState(false);
  const [onboardingState, setOnboardingState] = useState(null);
  const [welcomeOpen, setWelcomeOpen] = useState(false);
  const onboardedRef = useRef(false);

  // Refresh onboarding state from backend (auto-backfills milestones).
  // Includes `location.pathname` in the dep array so route changes
  // (e.g. user navigates from /admin to /) re-evaluate the modal gate.
  const reloadOnboarding = React.useCallback(async () => {
    if (!user) { setOnboardingState(null); setWelcomeOpen(false); return; }
    // Never pop the player-onboarding welcome modal on the /admin route —
    // the admin panel has its own password-only auth flow and is not part
    // of the regular player surface. Showing it there traps moderators in
    // the welcome modal with no way to reach the admin login form. Uses
    // the React-Router `location.pathname` (reactive) instead of
    // `window.location.pathname` (stale during SPA navigation).
    const onAdminRoute = (location?.pathname || "").startsWith("/admin");
    if (onAdminRoute) {
      setWelcomeOpen(false);
      return;
    }
    // Once the user dismisses the welcome modal in this browser session,
    // never re-show it — even if the server-side mark didn't persist yet
    // (otherwise we get a reload loop: close → mark → reload state →
    // mark not yet visible → reopen).
    if (onboardedRef.current) { setWelcomeOpen(false); return; }
    try {
      const d = await fetchOnboardingState();
      const ob = d.onboarding || {};
      setOnboardingState(ob);
      const done = new Set(ob.steps_completed || []);
      // Also respect a locally-stored dismissal flag so refreshes don't
      // re-pop the modal if the server's persistence lagged.
      let locallyDismissed = false;
      try {
        locallyDismissed = window.localStorage?.getItem("lh_welcome_dismissed") === "1";
      } catch (_e) { /* private mode → just skip */ }
      if (!done.has("welcome_seen") && !ob.dismissed && !ob.completed_at && !locallyDismissed) {
        setWelcomeOpen(true);
      } else {
        setWelcomeOpen(false);
      }
    } catch (_e) { /* non-blocking */ }
  }, [user, location?.pathname]);

  useEffect(() => { reloadOnboarding(); }, [reloadOnboarding]);

  // Notification deep-link: /leagues?lid={id}&tab={tab} should pop the
  // league detail modal automatically so the chat input the bell row was
  // pointing at is visible without an extra click.
  const [openLeagueInitialTab, setOpenLeagueInitialTab] = useState(null);
  useEffect(() => {
    const p = new URLSearchParams(location.search);
    const lid = p.get("lid");
    const tab = p.get("tab");
    if (lid) {
      setOpenLeagueId(lid);
      setOpenLeagueInitialTab(tab || null);
      // Strip the query params so future navigations don't keep reopening.
      const url = new URL(window.location.href);
      url.searchParams.delete("lid");
      url.searchParams.delete("tab");
      window.history.replaceState(null, "", url.pathname + (url.search || ""));
    }
  }, [location.search]);

  // After any profile/league/chat action, give the dashboard a chance to
  // refetch by listening for an app-wide event.
  useEffect(() => {
    const h = () => reloadOnboarding();
    window.addEventListener("lh:onboarding-bump", h);
    return () => window.removeEventListener("lh:onboarding-bump", h);
  }, [reloadOnboarding]);

  // MobileBottomNav and other components dispatch `lh:open-profile` to ask
  // the App shell to open the ProfilePanel modal at a given tab.
  useEffect(() => {
    const h = (e) => {
      const tab = (e?.detail && e.detail.tab) || "avatar";
      setOnboardingMode(false);
      setProfileInitialTab(typeof tab === "string" ? tab : null);
      setProfileOpen(true);
    };
    window.addEventListener("lh:open-profile", h);
    return () => window.removeEventListener("lh:open-profile", h);
  }, []);

  // Service worker registration for Web Push (Wave B). Idempotent — the
  // browser will skip if already registered. Safe in unsupported browsers.
  useEffect(() => {
    if (pushSupported()) {
      ensureServiceWorker().catch(() => {});
    }
  }, []);

  // Auth_error / success query param toast
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const err = p.get("auth_error");
    const ok = p.get("auth");
    if (err) {
      setToast(`Sign-in error: ${err}`);
      window.history.replaceState(null, "", window.location.pathname);
    } else if (ok === "success") {
      setToast("Welcome to the arena.");
      window.history.replaceState(null, "", window.location.pathname);
    }
  }, []);

  // Onboarding gate: open ProfilePanel for fresh users — but defer it on
  // invite redemption pages so the user can join the team/league first.
  const onInvitePage = location.pathname.startsWith("/invite/")
    || location.pathname.startsWith("/team-invite/");

  useEffect(() => {
    if (!user) { onboardedRef.current = false; return; }
    if (onboardedRef.current) return;
    if (onInvitePage) return; // let the invite flow finish first
    if (!isProfileComplete(user)) {
      onboardedRef.current = true;
      setOnboardingMode(true);
      setProfileInitialTab("avatar");
      setProfileOpen(true);
      setToast("Step 2/3 — set up your operator profile.");
    }
  }, [user, onInvitePage]);

  // What's-new auto-popup removed per user request — modal is still available
  // via the navbar / contact page links for users who want to view changelog.

  const requireAuth = () => { setToast("Sign in to continue."); setAuthOpen(true); };

  const openProfile = (tab) => {
    setOnboardingMode(false);
    setProfileInitialTab(typeof tab === "string" ? tab : null);
    setProfileOpen(true);
  };

  const triggerCreateLeague = () => {
    if (!user) { requireAuth(); return; }
    if (!isProfileComplete(user)) {
      setToast("Finish setting up your operator profile first.");
      setOnboardingMode(true);
      setProfileInitialTab("avatar");
      setProfileOpen(true);
      return;
    }
    navigate("/leagues");
    setCreateLeagueTrigger((n) => n + 1);
    // One-shot — clear immediately so navigating away and back doesn't reopen
    // the create-league modal on remount.
    setTimeout(() => setCreateLeagueTrigger(0), 1500);
  };

  return (
    <>
      <Navbar
        onSignIn={() => setAuthOpen(true)}
        onOpenProfile={openProfile}
        onOpenWhatsNew={() => setWhatsNewOpen(true)}
      />
      {React.cloneElement(children, {
        onToast: setToast,
        onRequireAuth: requireAuth,
        onRequireProfile: () => { setOnboardingMode(true); setProfileInitialTab("avatar"); setProfileOpen(true); },
        onOpenLeague: setOpenLeagueId,
        onCompleteProfile: () => openProfile("avatar"),
        // Dedicated route into the INBOX tab of the profile modal — the
        // dashboard's REVIEW button + the InboxBlock's OPEN INBOX CTA
        // both wire to this. Previously these buttons opened the AVATAR
        // tab (because they shared `onCompleteProfile`), which is why
        // "review box isn't clickable" — clicking it dropped users on
        // the avatar editor with no clue where the report went. Now
        // they land directly on the inbox feed where the report row
        // exposes BAN / ALLOW / MESSAGE actions.
        onOpenInbox: () => openProfile("inbox"),
        onCreateLeague: triggerCreateLeague,
        onSignIn: () => setAuthOpen(true),
        onScrollToLeagues: (anchor = "leagues") => {
          if (anchor === "matchmaking") { navigate("/matchmaking"); return; }
          navigate("/leagues");
        },
        onScrollToTeams: () => {
          navigate("/leagues");
          setTimeout(() => document.getElementById("my-hosted")?.scrollIntoView({ behavior: "smooth", block: "start" }), 200);
        },
        user,
        onboardingState,
        reloadOnboarding,
        openCreateOnMount: createLeagueTrigger > 0 ? createLeagueTrigger : undefined,
      })}

      <OnboardingWelcomeModal
        open={welcomeOpen && !(location?.pathname || "").startsWith("/admin")}
        userName={user?.name}
        onClose={() => {
          // Mark dismissed locally + in-memory so the modal can't reload
          // back on top of itself if the server-side persistence lags.
          onboardedRef.current = true;
          try { window.localStorage?.setItem("lh_welcome_dismissed", "1"); } catch (_e) { /* ignore */ }
          setWelcomeOpen(false);
        }}
      />

      <MobileBottomNav />

      <PWAInstallPrompt />

      <AdBanner />

      <LegalFooter onWhatsNew={() => setWhatsNewOpen(true)} />

      <CookieConsent />
      <AiConsentModal />

      <AuthModal open={authOpen} onClose={() => setAuthOpen(false)} />
      <ProfilePanel
        open={profileOpen}
        onClose={() => { setProfileOpen(false); setOnboardingMode(false); setProfileInitialTab(null); }}
        onSaved={setToast}
        onToast={setToast}
        onboarding={onboardingMode}
        initialTab={profileInitialTab}
      />
      <LeagueDetailModal
        leagueId={openLeagueId}
        initialTab={openLeagueInitialTab}
        onClose={() => { setOpenLeagueId(null); setOpenLeagueInitialTab(null); }}
        onToast={setToast}
        onRequireAuth={requireAuth}
      />
      <WhatsNewModal open={whatsNewOpen} onClose={() => setWhatsNewOpen(false)} />
      <Toast message={toast} onClose={() => setToast("")} />
    </>
  );
}

function HomeContent({ onToast, onRequireAuth, onRequireProfile, onOpenLeague,
                       onCompleteProfile, onCreateLeague, onSignIn,
                       onScrollToLeagues, onScrollToTeams,
                       user, openCreateOnMount,
                       onboardingState, reloadOnboarding }) {
  // Wait for /api/auth/me to resolve before deciding which view to
  // render. Without this guard, signed-in users briefly see the
  // marketing landing flash for ~2-3s on first paint while auth
  // hydrates (Issue #6).
  const { loading } = useAuth();
  if (loading) {
    return (
      <div
        data-testid="home-auth-loading"
        className="min-h-[60vh] flex items-center justify-center"
        aria-label="Loading session"
      >
        <div className="flex flex-col items-center gap-3">
          <div className="h-8 w-8 rounded-full border-2 border-[#14b8a6]/40 border-t-[#14b8a6] animate-spin" />
          <p className="text-[11px] tracking-widest text-[#14b8a6]/70">LOADING</p>
        </div>
      </div>
    );
  }
  // Authenticated users land on the dashboard (Facebook-style home).
  // Guests see the original marketing Hero so the funnel still works.
  if (user) {
    return (
      <PlayerDashboard
        onToast={onToast}
        onCompleteProfile={onCompleteProfile}
        onOpenLeague={onOpenLeague}
        onCreateLeague={onCreateLeague}
        onboardingState={onboardingState}
        reloadOnboarding={reloadOnboarding}
      />
    );
  }
  return (
    <Hero
      onSignIn={onSignIn}
      user={user}
      onCompleteProfile={onCompleteProfile}
      onCreateLeague={onCreateLeague}
      onScrollToLeagues={onScrollToLeagues}
      onScrollToTeams={onScrollToTeams}
      onOpenLeague={onOpenLeague}
      onToast={onToast}
    />
  );
}

function PublicOperatorRoute({ onToast }) {
  const { user } = useAuth();
  return <PublicOperatorPage onToast={onToast} user={user} />;
}

function AppRouter() {
  const location = useLocation();
  const [toast, setToast] = useState("");
  if (location.hash?.includes("session_id=")) {
    return (
      <>
        <AuthCallback onDone={setToast} />
        <Toast message={toast} onClose={() => setToast("")} />
      </>
    );
  }
  return (
    <Routes>
      <Route path="/" element={<Shell isHome={true}><HomeContent /></Shell>} />
      <Route path="/matchmaking" element={<Shell isHome={false}><MatchmakingPage /></Shell>} />
      <Route path="/leagues" element={<Shell isHome={false}><LeaguesPage /></Shell>} />
      <Route path="/lfg" element={<Shell isHome={false}><CategoryPage kind="lfg" /></Shell>} />
      <Route path="/players" element={<Shell isHome={false}><CategoryPage kind="players" /></Shell>} />
      <Route path="/profiles" element={<Shell isHome={false}><CategoryPage kind="profiles" /></Shell>} />
      <Route path="/pricing" element={<Shell isHome={false}><CategoryPage kind="pricing" /></Shell>} />
      <Route path="/contact" element={<Shell isHome={false}><ContactPage /></Shell>} />
      <Route path="/developers" element={<Shell isHome={false}><DeveloperPortalPage /></Shell>} />
      <Route path="/settings/developer" element={<Shell isHome={false}><DeveloperSettingsPage /></Shell>} />
      <Route path="/analytics" element={<Shell isHome={false}><CategoryPage kind="analytics" /></Shell>} />
      <Route path="/leaderboards" element={<Shell isHome={false}><CategoryPage kind="leaderboards" /></Shell>} />
      <Route path="/admin" element={<Shell isHome={false}><AdminPanel onToast={setToast} /></Shell>} />
      <Route path="/season-pass" element={<Shell isHome={false}><SeasonPassPage onToast={setToast} /></Shell>} />
      <Route path="/invite/:token" element={<Shell isHome={false}><InvitePage /></Shell>} />
      <Route path="/team-invite/:token" element={<Shell isHome={false}><TeamInvitePage /></Shell>} />
      <Route path="/r/:code" element={<Shell isHome={false}><RefLandingPage /></Shell>} />
      <Route path="/r/grant/:token" element={<Shell isHome={false}><GrantRedeemPage /></Shell>} />
      <Route path="/hqs" element={<Shell isHome={false}><HqDiscoveryPage /></Shell>} />
      {/* Bookmark/share aliases — `/teams` and `/rankings` always 404'd
          because they fell through to the `/:slug` catch-all and tried to
          resolve as league slugs. Redirect to the canonical pages. */}
      <Route path="/teams" element={<Navigate to="/hqs" replace />} />
      <Route path="/rankings" element={<Navigate to="/leaderboards" replace />} />

      {/* Legal pages — see /app/LEGAL_SETUP.md for the launch checklist. */}
      <Route path="/legal/privacy"       element={<Shell isHome={false}><PrivacyPolicy /></Shell>} />
      <Route path="/legal/terms"         element={<Shell isHome={false}><TermsOfService /></Shell>} />
      <Route path="/legal/cookies"       element={<Shell isHome={false}><CookiePolicy /></Shell>} />
      <Route path="/legal/community"     element={<Shell isHome={false}><CommunityGuidelines /></Shell>} />
      <Route path="/legal/contact-info"  element={<Shell isHome={false}><ContactInfo /></Shell>} />
      <Route path="/legal/subscriptions" element={<Shell isHome={false}><SubscriptionTerms /></Shell>} />
      <Route path="/legal/withdrawal"    element={<Shell isHome={false}><WithdrawalPolicy /></Shell>} />

      <Route path="/rankings/leagues/:slug" element={<Shell isHome={false}><LeagueLadderPage /></Shell>} />
      <Route path="/teams/:team_id" element={<Shell isHome={false}><TeamPage onToast={setToast} /></Shell>} />
      <Route path="/players/:user_id" element={<Shell isHome={false}><PlayerPublicPage onToast={setToast} /></Shell>} />
      <Route path="/u/:handle" element={<Shell isHome={false}><PublicOperatorRoute onToast={setToast} /></Shell>} />
      <Route path="/dashboard" element={<Shell isHome={false}><HomeContent /></Shell>} />
      <Route path="/wallet" element={<Shell isHome={false}><WalletPage onToast={setToast} /></Shell>} />
      <Route path="/:slug" element={<Shell isHome={false}><LeagueBySlugPage /></Shell>} />
      <Route path="*" element={<Shell isHome={false}><HomeContent /></Shell>} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <FeatureFlagsProvider>
        <AppRouter />
        <CommandPalette />
      </FeatureFlagsProvider>
    </AuthProvider>
  );
}
